import React from 'react'

import {
  BaseProps,
  BoxCssProps,
  BoxNonCssProps,
  BoxProps,
  ColorRelatedProps,
  RadiusesPropsType,
  ShadowPropsType,
  SpacingPropsType
} from '../../../../../types/Box'
import {cssTheme} from '../../../../themes'
import {Breakpoints} from '../../../../utils/breakpoints'
import {BOX_CSS_PROPS_KEYS, PREDEFINED_CLASSES} from './constants'

export const getSpacingValue = (value?: string | SpacingPropsType): string => {
  if (!value) return ''
  if (value in cssTheme.layout.spacing) {
    return cssTheme.layout.spacing[value as SpacingPropsType]
  }
  return value as string
}

export const getSpacing = (
  props: BoxCssProps,
  name: keyof BaseProps,
  breakpoints?: Breakpoints
): string => {
  const spacingValue =
    breakpoints && props?.[breakpoints]?.[name] !== undefined
      ? props[breakpoints][name]
      : props[name]

  return getSpacingValue(spacingValue as string)
}

export const getBorderRadiusValue = (value: RadiusesPropsType): string => {
  if (!value) return ''
  return cssTheme.layout.radius[value]
}

export const getBorderRadius = (
  props: BoxCssProps,
  breakpoints?: Breakpoints
) => {
  const borderRadiusValue =
    breakpoints && props?.[breakpoints]?.borderRadius !== undefined
      ? props[breakpoints].borderRadius
      : props.borderRadius

  if (!borderRadiusValue) return ''
  return getBorderRadiusValue(borderRadiusValue)
}

export const getColor = (
  props: BoxCssProps,
  name: ColorRelatedProps,
  breakpoints?: Breakpoints
) => {
  const colorKey = breakpoints ? props?.[breakpoints]?.[name] : props?.[name]

  if (!colorKey) return undefined
  return (
    colorKey
      .split('.')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .reduce((acc: any, part: string) => acc && acc[part], cssTheme.colors)
  )
}

const getShadow = (props: BoxCssProps, breakpoints?: Breakpoints) => {
  const shadowKey = breakpoints ? props?.[breakpoints]?.shadow : props?.shadow

  if (!shadowKey) return undefined
  return cssTheme.shadows[shadowKey as ShadowPropsType]
}

const getPropertyStringValue = (
  props: BoxCssProps,
  name: keyof BaseProps,
  breakpoints?: Breakpoints
) => {
  if (breakpoints) return props?.[breakpoints]?.[name]
  return props?.[name]
}

const getValueBasedType = (
  props: BoxCssProps,
  propName: keyof BaseProps,
  breakpoint?: Breakpoints
) => {
  switch (propName) {
    case 'backgroundColor':
    case 'color':
    case 'borderColor':
    case 'outlineColor':
      return getColor(props, propName, breakpoint)
    case 'gridColumn':
    case 'gridRow':
    case 'gridAutoRows':
    case 'gridTemplateColumns':
    case 'zIndex':
    case 'transition':
    case 'flex':
    case 'outlineOffset':
      return getPropertyStringValue(props, propName, breakpoint)
    case 'shadow':
      return getShadow(props, breakpoint)
    case 'borderRadius':
      return getBorderRadius(props, breakpoint)
    default:
      return getSpacing(props, propName, breakpoint)
  }
}

export const getPredefinedClasses = (breakpoint?: Breakpoints): string => {
  return PREDEFINED_CLASSES.map(({key, property, values}) =>
    values.map(value => {
      return `
        &.box-${key}${breakpoint ? `-${value}-${breakpoint}` : `-${value}`} {
          ${property}: ${value};
        }
      `
    })
  ).join('\n')
}

export const getPredefinedShadowClasses = (
  breakpoint?: Breakpoints
): string => {
  return Object.keys(cssTheme.shadows)
    .map(key => {
      const shadow = key as ShadowPropsType
      return `
        &.box-shadow${breakpoint ? `-${shadow}-${breakpoint}` : `-${shadow}`} {
          box-shadow: ${cssTheme.shadows[shadow]};
        }
      `
    })
    .join('\n')
}

export const getSplittedProps = (
  props: BoxProps
): {cssProps: BoxCssProps; baseProps: BoxNonCssProps} => {
  let cssProps: Partial<BoxCssProps> = {}
  let baseProps: BoxNonCssProps = {
    children: props.children,
    className: props.className,
    hasMirror: props.hasMirror
  }

  const {
    desktopLg,
    desktopMd,
    desktopSm,
    desktopXs,
    mobileLg,
    mobileSm,
    ...restProps
  } = props

  Object.keys(restProps).forEach(key => {
    if (BOX_CSS_PROPS_KEYS.has(key as keyof BaseProps)) {
      cssProps = {
        ...cssProps,
        [key]: restProps[key as keyof BaseProps]
      }
    } else {
      baseProps = {
        ...baseProps,
        [key]: restProps[key as keyof BoxNonCssProps]
      }
    }
  })

  return {
    cssProps: {
      desktopLg,
      desktopMd,
      desktopSm,
      desktopXs,
      mobileLg,
      mobileSm,
      ...cssProps
    },
    baseProps
  }
}

const generateBaseCssClasses = (
  classes: Set<string>,
  props?: BoxCssProps,
  layout?: Breakpoints
) => {
  if (!props) {
    return
  }

  if (layout) {
    Object.entries(props).forEach(([key, value]) => {
      if (value) {
        classes.add(`box_${key}_${layout}`)
      }
    })
    return
  }
  Object.entries(props).forEach(([key, value]) => {
    if (value) {
      classes.add(`box_${key}`)
    }
  })
}

export const generateBoxCssClasses = (props: BoxCssProps): string[] => {
  const availableClasses = new Set<string>()

  Object.entries(props).forEach(([key, value]) => {
    if (
      key === 'mobileSm' ||
      key === 'mobileLg' ||
      key === 'desktopXs' ||
      key === 'desktopSm' ||
      key === 'desktopMd' ||
      key === 'desktopLg'
    ) {
      generateBaseCssClasses(
        availableClasses,
        value as BoxCssProps,
        key as Breakpoints
      )
    } else {
      availableClasses.add(`box_${key}`)
    }
  })

  return [...availableClasses]
}

const getBoxCssVariables = (props: BoxCssProps, breakpoint?: Breakpoints) => {
  return [...BOX_CSS_PROPS_KEYS].reduce<React.CSSProperties>((a, v) => {
    const value = getValueBasedType(props, v, breakpoint)

    if (!value) return a
    if (breakpoint && !props[breakpoint]?.[v]) return a
    if (!breakpoint && !props?.[v]) return a

    return {
      ...a,
      [`--${v}${breakpoint ? `-${breakpoint}` : ''}`]: value
    }
  }, {})
}

export const generateBoxStyles = (props: BoxCssProps) => {
  return {
    ...getBoxCssVariables(props),
    ...getBoxCssVariables(props, Breakpoints.mobileLg),
    ...getBoxCssVariables(props, Breakpoints.desktopXs),
    ...getBoxCssVariables(props, Breakpoints.desktopSm),
    ...getBoxCssVariables(props, Breakpoints.desktopMd),
    ...getBoxCssVariables(props, Breakpoints.desktopLg)
  }
}
